import { useState } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { createInvitation } from '../../api/users';
import type { CreateInvitationPayload } from '../../api/users';

interface Props {
  onClose: () => void;
  onSuccess: () => void;
}

const ROLE_OPTIONS: { value: CreateInvitationPayload['role']; labelKey: string }[] = [
  { value: 'AMM', labelKey: 'utenti.invita_modal.ruolo_amm' },
  { value: 'COMMERCIALE', labelKey: 'utenti.invita_modal.ruolo_commerciale' },
  { value: 'RT', labelKey: 'utenti.invita_modal.ruolo_rt' },
  { value: 'DIR', labelKey: 'utenti.invita_modal.ruolo_dir' },
  { value: 'OP', labelKey: 'utenti.invita_modal.ruolo_op' },
];

export default function InvitaUtenteModal({ onClose, onSuccess }: Props) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<CreateInvitationPayload['role'] | ''>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !role) return;

    setLoading(true);
    setError(null);
    try {
      await createInvitation({ email, role });
      onSuccess();
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const detail = err.response?.data?.detail ?? '';
        if (status === 409) {
          setError(detail || t('utenti.invita_modal.err_409'));
        } else if (status === 502) {
          setError(t('utenti.invita_modal.err_email'));
        } else {
          setError(t('utenti.invita_modal.err_generico'));
        }
      } else {
        setError(t('utenti.invita_modal.err_generico'));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-mid">
          <h2 className="text-base font-semibold text-ink-dark">{t('utenti.invita_modal.titolo')}</h2>
          <button type="button" onClick={onClose} className="text-ink-soft hover:text-ink-dark">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6 py-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink-dark mb-1">
                {t('utenti.invita_modal.email')} <span className="text-accent-danger">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder={t('utenti.invita_modal.email_placeholder')}
                className="w-full px-3 py-2 text-sm border border-stone-border rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/40 focus:border-sage"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-dark mb-1">
                {t('utenti.invita_modal.ruolo')} <span className="text-accent-danger">*</span>
              </label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as CreateInvitationPayload['role'])}
                required
                className="w-full px-3 py-2 text-sm border border-stone-border rounded-lg focus:outline-none focus:ring-2 focus:ring-sage/40 focus:border-sage bg-white"
              >
                <option value="">{t('utenti.invita_modal.ruolo_seleziona')}</option>
                {ROLE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {t(opt.labelKey)}
                  </option>
                ))}
              </select>
            </div>

            <p className="text-xs text-ink-soft">
              {t('utenti.invita_modal.nota')}
            </p>

            {error && (
              <div className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger">
                {error}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-stone-mid bg-stone-bg">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50"
            >
              {t('common.azioni.annulla')}
            </button>
            <button
              type="submit"
              disabled={loading || !email || !role}
              className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors disabled:opacity-60"
            >
              {loading ? t('utenti.invita_modal.invio') : t('utenti.invita_modal.invia')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
